import { Component, type ErrorInfo, type ReactNode } from 'react';
import styles from './AppErrorBoundary.module.css';

/**
 * Where in the tree the boundary sits. Drives the copy and the recovery
 * actions offered:
 *
 * - `app`     — wraps the whole SPA. Nothing above it can render, so the
 *               fallback owns the viewport and offers reload + home.
 * - `page`    — wraps a routed page inside a layout (see RouteErrorBoundary).
 *               The navbar / sidebar around it keep working, so the user can
 *               simply click to another tab; we still offer try-again + reload.
 * - `section` — wraps one widget in the shell (e.g. the critical-alert bar).
 *               A crash there must not take the page with it; the fallback is
 *               a single quiet line with a retry.
 */
export type ErrorBoundaryScope = 'app' | 'page' | 'section';

export interface AppErrorBoundaryProps {
  children: ReactNode;
  scope?: ErrorBoundaryScope;
  /** Human label for the guarded area, used in the fallback copy ("Critical alerts"). */
  label?: string;
  /**
   * When this value changes the boundary clears its error and re-renders the
   * children. RouteErrorBoundary feeds the pathname so navigating away from
   * a broken page recovers automatically.
   */
  resetKey?: unknown;
  /** Where "Go to home" points for app/page scopes. Plain href so it works outside a router. */
  homeHref?: string;
  /** Called after the boundary catches, for telemetry. Never throws through. */
  onError?: (error: Error, info: ErrorInfo, scope: ErrorBoundaryScope) => void;
}

interface State {
  error: Error | null;
}

const COPY: Record<ErrorBoundaryScope, { title: string; body: string }> = {
  app: {
    title: 'Something went wrong',
    body: 'The portal hit an unexpected error and could not draw this screen. Reloading usually fixes it; nothing you submitted has been lost.',
  },
  page: {
    title: 'This page could not be displayed',
    body: 'An unexpected error stopped this page from rendering. The rest of the portal still works: try again, reload, or switch to another tab.',
  },
  section: {
    title: 'could not load',
    body: '',
  },
};

/**
 * Recoverable React error boundary for the ChemMasters SPA.
 *
 * Before this existed a single `TypeError` anywhere in the render tree (for
 * example `/api/auth/me` omitting `scopes`, or `/api/notifications/critical`
 * answering without `alerts`) unmounted the WHOLE React root and left CSRs
 * looking at a blank white page with no message and no way back short of a
 * hard reload (kanban 90f3b9d8). This boundary contains the failure to the
 * scope it wraps and always offers a way forward.
 *
 * Class component by necessity: React only exposes getDerivedStateFromError /
 * componentDidCatch on classes.
 */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const scope = this.props.scope ?? 'page';
    console.error(`[AppErrorBoundary:${scope}] render error`, error, info);
    try {
      this.props.onError?.(error, info, scope);
    } catch (hookErr) {
      // A telemetry hook must never turn a contained error into a fatal one.
      console.error('[AppErrorBoundary] onError hook threw', hookErr);
    }
  }

  componentDidUpdate(prevProps: AppErrorBoundaryProps): void {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private handleRetry = () => {
    this.setState({ error: null });
  };

  private handleReload = () => {
    window.location.reload();
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const scope = this.props.scope ?? 'page';
    const label = this.props.label;
    const homeHref = this.props.homeHref ?? '/';
    const message = error.message || String(error);

    if (scope === 'section') {
      return (
        <div
          className={styles.section}
          role="alert"
          data-testid="app-error-boundary"
          data-scope="section"
        >
          <span className={styles.sectionText}>
            {label ?? 'This section'} {COPY.section.title}.
          </span>
          <button type="button" className={styles.linkBtn} onClick={this.handleRetry}>
            Try again
          </button>
        </div>
      );
    }

    const copy = COPY[scope];
    return (
      <div
        className={scope === 'app' ? styles.app : styles.page}
        role="alert"
        data-testid="app-error-boundary"
        data-scope={scope}
      >
        <div className={styles.card}>
          <h1 className={styles.title}>{copy.title}</h1>
          <p className={styles.body}>{copy.body}</p>
          <div className={styles.actions}>
            <button type="button" className={styles.primaryBtn} onClick={this.handleReload}>
              Reload page
            </button>
            <button type="button" className={styles.secondaryBtn} onClick={this.handleRetry}>
              Try again
            </button>
            <a className={styles.secondaryBtn} href={homeHref}>
              Go to home
            </a>
          </div>
          {/* The raw message helps a CSR give support something concrete to
              search for; it is tucked behind a disclosure so it never reads
              as the primary content. */}
          <details className={styles.details}>
            <summary className={styles.summary}>Technical details</summary>
            <pre className={styles.pre} data-testid="app-error-boundary-message">
              {message}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
